import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/c98bf352-e1ac-48f2-985a-53503ade1afd.png';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const navLinks = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.services'), path: '/services' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  const currentLang = i18n.language === 'zh' ? 'zh' : 'en';

  const toggleLang = () => {
    const next = currentLang === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(next);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'
      }`}
      style={{ height: 80 }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="w-10 h-10 flex items-center justify-center">
            <img
              src={LOGO_URL}
              alt="Armenia Relocation Logo"
              className="w-10 h-10 object-contain"
            />
          </div>
          <div className="flex flex-col leading-tight">
            <span
              className={`font-bold text-base tracking-tight transition-colors duration-300 ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              ArmeniaSettle
            </span>
            <span
              className={`text-xs font-normal transition-colors duration-300 ${
                scrolled ? 'text-gray-500' : 'text-white/80'
              }`}
            >
              {t('nav.tagline')}
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === link.path
                  ? scrolled
                    ? 'text-blue-600'
                    : 'text-white font-semibold'
                  : scrolled
                  ? 'text-gray-700 hover:text-blue-600'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right Controls */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Switcher */}
          <button
            onClick={toggleLang}
            className={`whitespace-nowrap text-sm font-semibold px-3 py-1.5 rounded-full border transition-all duration-200 cursor-pointer ${
              scrolled
                ? 'border-gray-200 text-gray-700 hover:border-blue-400 hover:text-blue-600 bg-white'
                : 'border-white/40 text-white/90 hover:border-white hover:text-white bg-white/10'
            }`}
          >
            {currentLang === 'en' ? t('lang.zh') : t('lang.en')}
          </button>

          {/* CTA */}
          <button
            onClick={() => navigate('/contact')}
            className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-2.5 rounded-full transition-all duration-200 cursor-pointer"
          >
            {t('nav.cta')}
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggleLang}
            className={`whitespace-nowrap text-xs font-semibold px-2.5 py-1 rounded-full border transition-all duration-200 cursor-pointer ${
              scrolled
                ? 'border-gray-200 text-gray-700 bg-white'
                : 'border-white/40 text-white/90 bg-white/10'
            }`}
          >
            {currentLang === 'en' ? t('lang.zh') : t('lang.en')}
          </button>
          <button
            className={`w-8 h-8 flex items-center justify-center cursor-pointer transition-colors duration-200 ${
              scrolled ? 'text-gray-700' : 'text-white'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line ri-lg`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium py-2 cursor-pointer ${
                  location.pathname === link.path ? 'text-blue-600' : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => navigate('/contact')}
              className="whitespace-nowrap bg-blue-600 text-white text-sm font-medium px-6 py-3 rounded-full mt-2 cursor-pointer"
            >
              {t('nav.cta')}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
